import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MessageCircle, MapPin, Phone, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

export function Contact() {
  const { t } = useTranslation();
  const position: [number, number] = [6.2254673, -75.5348959];

  return (
    <div className="min-h-screen bg-zinc-950 pt-32 pb-20 px-6 sm:px-12 lg:px-20">
      
      {/* Header */}
      <div className="text-center mb-16 md:mb-24">
        <h1 className="font-neocat text-4xl md:text-6xl text-white mb-6 tracking-widest uppercase drop-shadow-xl">
          {t("contact.title")}
        </h1>
        <div className="w-16 md:w-24 h-[2px] bg-zinc-700 mx-auto"></div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-12">
        
        {/* Left Side: Info & Socials */}
        <div className="flex flex-col justify-center order-2 lg:order-1">
          <h2 className="font-neocat text-2xl md:text-3xl text-white mb-8 tracking-wide">
            {t("contact.subtitle")}
          </h2>
          <p className="text-zinc-400 font-sans font-light leading-relaxed mb-12 max-w-lg">
            {t("contact.description")}
          </p>

          {/* Contact Details */}
          <div className="space-y-8 mb-16">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-300 shrink-0">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-neocat tracking-wider text-sm mb-1">{t("contact.address_label")}</h3>
                <p className="text-zinc-400 font-light text-sm leading-relaxed mb-4">
                  {t("contact.address_line1")}<br />
                  {t("contact.address_line2")}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${position[0]},${position[1]}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-500 hover:bg-zinc-800 transition-all text-xs font-sans font-medium tracking-wide uppercase text-zinc-300">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 0C7.582 0 4 3.582 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.418-3.582-8-8-8zm0 11.5c-1.933 0-3.5-1.567-3.5-3.5s1.567-3.5 3.5-3.5 3.5 1.567 3.5 3.5-1.567 3.5-3.5 3.5z"/></svg>
                    Google Maps
                  </a>
                  <a href={`https://waze.com/ul?ll=${position[0]},${position[1]}&navigate=yes`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-500 hover:bg-zinc-800 transition-all text-xs font-sans font-medium tracking-wide uppercase text-zinc-300">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 0C5.383 0 0 5.383 0 12c0 2.518.796 4.863 2.15 6.786l-1.905 4.398 4.793-1.077c1.921 1.042 4.108 1.637 6.438 1.637 6.617 0 12-5.383 12-12S18.617 0 12 0zm-3 15.5c-1.381 0-2.5-1.119-2.5-2.5s1.119-2.5 2.5-2.5 2.5 1.119 2.5 2.5-1.119 2.5-2.5 2.5zm6 0c-1.381 0-2.5-1.119-2.5-2.5s1.119-2.5 2.5-2.5 2.5 1.119 2.5 2.5-1.119 2.5-2.5 2.5zM17 9.5H7v-1h10v1z"/></svg>
                    Waze
                  </a>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-300 shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-neocat tracking-wider text-sm mb-1">{t("contact.phone_label")}</h3>
                <a href="https://api.whatsapp.com/send/?phone=573217757120" target="_blank" rel="noopener noreferrer" className="text-zinc-400 font-light text-sm hover:text-white transition-colors">
                  +57 321 775 7120
                </a>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full border border-zinc-700 flex items-center justify-center text-zinc-300 shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-neocat tracking-wider text-sm mb-1">{t("contact.email_label")}</h3>
                <a href="mailto:contacto@seminariomedellin.org" className="text-zinc-400 font-light text-sm hover:text-white transition-colors">
                  contacto@seminariomedellin.org
                </a>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div>
            <h3 className="text-white font-neocat tracking-widest text-xs mb-6 uppercase">{t("contact.socials_label")}</h3>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/SeminarioRedemptorisMaterMedellin/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-zinc-900 border border-zinc-800 hover:border-zinc-500 hover:bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-all duration-300">
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/redemptorismatermedellin/?hl=es" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-zinc-900 border border-zinc-800 hover:border-zinc-500 hover:bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-all duration-300">
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.youtube.com/c/RedemptorisMaterMedellin" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-zinc-900 border border-zinc-800 hover:border-zinc-500 hover:bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-all duration-300">
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://api.whatsapp.com/send/?phone=573217757120&text&type=phone_number&app_absent=0" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-zinc-900 border border-zinc-800 hover:border-zinc-500 hover:bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-all duration-300">
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Right Side: Dark Mode Map */}
        <div className="order-1 lg:order-2 h-[400px] lg:h-auto min-h-[500px] w-full bg-zinc-900 border border-zinc-800 relative z-0 overflow-hidden shadow-2xl">
          <style>{`
            .leaflet-tile {
              filter: brightness(180%) contrast(120%) saturate(0%) !important;
            }
          `}</style>
          <MapContainer 
            center={position} 
            zoom={15} 
            scrollWheelZoom={false} 
            className="w-full h-full"
          >
            {/* Dark Matter tiles from CartoDB */}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_3na2_1_b714feb0cd16b2b2e073842f"
            />
            <Marker position={position}>
              <Popup>
                <div className="text-center font-sans">
                  <strong className="font-neocat text-zinc-900">{t("contact.popup_title")}</strong><br />
                  {t("contact.popup_address")}
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
